"use client";

import Link, { type LinkProps } from "@/infrastructure/router/Link";
import { useEffect, useMemo, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	EmptyState,
	GlassCard,
	UnderlineTabBar,
} from "@/components/design-system/ArtistDesignSystem";
import {
	IP_CONTENT_TAB_LABELS,
	type IPContentTab,
	type IPNoteRow,
	type IPPlaylistItem,
	type IPTrackRow,
	WORK_FILTER_LABELS,
	type WorkDisplayFilter,
} from "./makeCatalog";
import { browserDiscoveryRepository } from "@/domain/discovery/DiscoveryRepository";
import { listLocalNotes } from "@/domain/social/LocalNoteStore";

export interface MakeIPTabProps {
	readonly contentTab: IPContentTab;
	readonly onContentTabChange: (next: IPContentTab) => void;
}

function formatDuration(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return "—";
	const minutes = Math.floor(seconds / 60);
	const remainder = Math.floor(seconds % 60);
	return `${minutes}:${remainder.toString().padStart(2, "0")}`;
}

function artworkGradient(artwork: "violet" | "amber" | "blue" | "lime" | "rose"): string {
	const gradients = {
		violet: "linear-gradient(135deg, #7373F2 0%, #E0589E 100%)",
		amber: "linear-gradient(135deg, #F5A623 0%, #E0589E 100%)",
		blue: "linear-gradient(135deg, #3A8DDE 0%, #7373F2 100%)",
		lime: "linear-gradient(135deg, #3DDC97 0%, #7373F2 100%)",
		rose: "linear-gradient(135deg, #E0589E 0%, #F5A623 100%)",
	} as const;
	return gradients[artwork];
}

export function MakeIPTab({
	contentTab,
	onContentTabChange,
}: Readonly<MakeIPTabProps>) {
	const session = useArtistSession();
	const [filter, setFilter] = useState<WorkDisplayFilter>("all");
	const [tracks, setTracks] = useState<readonly IPTrackRow[]>([]);
	const [notes, setNotes] = useState<readonly IPNoteRow[]>([]);

	useEffect(() => {
		let active = true;
		if (session.phase !== "user" || session.scope === null) {
			setTracks([]);
			setNotes([]);
			return;
		}
		Promise.all([
			browserDiscoveryRepository.loadHome(session.scope),
			listLocalNotes(session.scope),
		])
			.then(([snapshot, storedNotes]) => {
				if (!active) return;
				setTracks(
					snapshot.works.map((track) => ({
						id: track.id,
						title: track.title,
						artist: track.artist,
						duration: formatDuration(track.durationSeconds),
						isPublic: false,
						artworkGradient: artworkGradient(track.artwork),
						playbackHref:
							track.playableRequestId === null
								? null
								: `/player?request=${encodeURIComponent(track.playableRequestId)}`,
					})),
				);
				setNotes(
					storedNotes.map((note) => ({
						id: note.id,
						title:
							note.body.split(/\r?\n/, 1)[0]?.trim().slice(0, 60) ||
							"未命名笔记",
						excerpt: note.body,
						updatedAt: new Intl.DateTimeFormat(undefined, {
							month: "short",
							day: "numeric",
						}).format(new Date(note.updatedAt)),
					})),
				);
			})
			.catch(() => {
				if (active) {
					setTracks([]);
					setNotes([]);
				}
			});
		return () => {
			active = false;
		};
	}, [session.phase, session.scope]);

	const playlists = useMemo<readonly IPPlaylistItem[]>(
		() => [
			{ id: "liked", name: "我喜欢的", color: "#E0589E", icon: "heart", count: 0 },
			{ id: "works", name: "我的作品", color: "#7373F2", icon: "music", count: tracks.length },
			{ id: "notes", name: "我的笔记", color: "#3DDC97", icon: "waveform", count: notes.length },
		],
		[notes.length, tracks.length],
	);

	const visibleTracks = tracks.filter((track) => {
		if (filter === "published") return track.isPublic;
		if (filter === "unpublished") return !track.isPublic;
		return true;
	});

	return (
		<div className="make-ip-tab">
			<section className="make-ip-tab__gallery" aria-label="收藏与歌单">
				<ul className="make-ip-tab__gallery-list">
					{playlists.map((playlist) => (
						<li key={playlist.id}>
							<IPPlaylistCard item={playlist} />
						</li>
					))}
				</ul>
			</section>

			<div className="make-ip-tab__subtabs">
				<UnderlineTabBar
					tabs={["songs", "notes"] as const}
					selected={contentTab}
					onSelect={(value) => onContentTabChange(value as IPContentTab)}
					label={(value) => IP_CONTENT_TAB_LABELS[value as IPContentTab]}
				/>
			</div>

			{contentTab === "songs" ? (
				<section className="make-ip-tab__songs" aria-label="我的歌曲">
					<header className="make-ip-tab__section-header">
						<h2>我的歌曲</h2>
						<label className="make-ip-tab__filter">
							<span>{WORK_FILTER_LABELS[filter]}</span>
							<ArtistIcon name="sliders" size={14} />
							<select
								aria-label="筛选作品"
								value={filter}
								onChange={(event) =>
									setFilter(event.target.value as WorkDisplayFilter)
								}
							>
								{(Object.keys(WORK_FILTER_LABELS) as WorkDisplayFilter[]).map(
									(value) => (
										<option key={value} value={value}>
											{WORK_FILTER_LABELS[value]}
										</option>
									),
								)}
							</select>
						</label>
					</header>

					{visibleTracks.length === 0 ? (
						<EmptyState
							title="还没有作品"
							message="切到「创作」生成你的第一首歌，或切到「上传」发布已有音频。"
							symbol={<ArtistIcon name="music" size={36} />}
						/>
					) : (
						<GlassCard className="make-ip-tab__list-card">
							<ul className="make-ip-tab__track-list">
								{visibleTracks.map((track, index) => (
									<li key={track.id}>
										<IPTrackRowItem track={track} />
										{index < visibleTracks.length - 1 ? (
											<span
												className="make-ip-tab__divider"
												aria-hidden="true"
											/>
										) : null}
									</li>
								))}
							</ul>
						</GlassCard>
					)}
				</section>
			) : (
				<section className="make-ip-tab__notes" aria-label="我的笔记">
					<header className="make-ip-tab__section-header">
						<h2>我的笔记</h2>
					</header>
					{notes.length === 0 ? (
						<EmptyState
							title="还没有笔记"
							message="在创作过程中保存的灵感片段会出现在这里。"
							symbol={<ArtistIcon name="doc" size={36} />}
						/>
					) : (
						<GlassCard className="make-ip-tab__list-card">
							<ul className="make-ip-tab__note-list">
								{notes.map((note, index) => (
									<li key={note.id}>
										<IPNoteRowItem note={note} />
										{index < notes.length - 1 ? (
											<span
												className="make-ip-tab__divider"
												aria-hidden="true"
											/>
										) : null}
									</li>
								))}
							</ul>
						</GlassCard>
					)}
				</section>
			)}
		</div>
	);
}

function IPPlaylistCard({ item }: Readonly<{ item: IPPlaylistItem }>) {
	return (
		<article
			className="make-ip-tab__playlist-card"
			style={{
				background: `linear-gradient(135deg, ${item.color} 0%, color-mix(in srgb, ${item.color} 35%, var(--ds-surface)) 100%)`,
			}}
		>
			<span className="make-ip-tab__playlist-icon" aria-hidden="true">
				<ArtistIcon name={item.icon} size={22} />
			</span>
			<div className="make-ip-tab__playlist-copy">
				<strong>{item.name}</strong>
				<small>{item.count} 项</small>
			</div>
		</article>
	);
}

function IPTrackRowItem({ track }: Readonly<{ track: IPTrackRow }>) {
	return (
		<div className="make-ip-tab__track-row">
			{track.playbackHref === null ? (
				<button
					type="button"
					className="make-ip-tab__track-artwork"
					style={{ background: track.artworkGradient }}
					aria-label="该作品暂不可播放"
					disabled
				>
					<ArtistIcon name="music" size={18} />
				</button>
			) : (
				<Link
					className="make-ip-tab__track-artwork"
					style={{ background: track.artworkGradient }}
					aria-label={`播放 ${track.title}`}
					href={track.playbackHref}
				>
					<ArtistIcon name="play" size={18} />
				</Link>
			)}
			<div className="make-ip-tab__track-copy">
				<strong>{track.title}</strong>
				<small>
					{track.artist} · {track.duration}
				</small>
			</div>
			<span className="make-ip-tab__track-meta">
				{track.isPublic ? "已发布" : "未发布"}
			</span>
			<Link
				className="ds-icon-button"
				aria-label={`编辑 ${track.title}`}
				href={`/track/edit?id=${encodeURIComponent(track.id)}` as LinkProps<string>["href"]}
			>
				<ArtistIcon name="more" size={18} />
			</Link>
		</div>
	);
}

function IPNoteRowItem({ note }: Readonly<{ note: IPNoteRow }>) {
	return (
		<Link
			className="make-ip-tab__note-row"
			href={`/community/thread/${encodeURIComponent(note.id)}` as LinkProps<string>["href"]}
		>
			<span className="make-ip-tab__note-icon" aria-hidden="true">
				<ArtistIcon name="doc" size={20} />
			</span>
			<div className="make-ip-tab__note-copy">
				<strong>{note.title}</strong>
				<small>{note.excerpt}</small>
			</div>
			<span className="make-ip-tab__note-time">{note.updatedAt}</span>
		</Link>
	);
}
